// Carrusel de productos MAJA: elementos compartidos por las secciones con
// .maja-product-carousel (ver maja-product-carousel.css). No modifican
// carousel-dots ni scroll-carousel del theme.
const MOBILE_QUERY = '(max-width: 699.98px)';

// Dots por página. En móvil se limitan a dos, cada uno cubre la mitad de las páginas.
class MajaCarouselDots extends HTMLElement {
  #abortController;
  #resizeObserver;
  #frame;

  async connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    await customElements.whenDefined('scroll-carousel');
    if (signal.aborted || !this.isConnected || !this.carousel) return;

    const schedule = () => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.rebuild());
    };
    this.carousel.addEventListener('carousel:select', schedule, { signal });
    this.carousel.addEventListener('carousel:filter', schedule, { signal });
    this.carousel.addEventListener('scroll', schedule, { passive: true, signal });
    this.#resizeObserver = new ResizeObserver(schedule);
    this.#resizeObserver.observe(this.carousel);
    if (this.carousel.firstElementChild) this.#resizeObserver.observe(this.carousel.firstElementChild);
    schedule();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
    this.replaceChildren();
  }

  get carousel() {
    return document.getElementById(this.getAttribute('aria-controls'));
  }

  get columns() {
    return Math.max(1, Number(getComputedStyle(this.carousel).getPropertyValue('--maja-cards-per-row')) || 1);
  }

  get perPage() {
    const rows = Math.max(1, Number(getComputedStyle(this.carousel).getPropertyValue('--product-list-rows')) || 1);
    return this.columns * rows;
  }

  get totalCells() {
    return this.carousel?.cells?.length ?? 0;
  }

  get realPageCount() {
    return Math.max(1, Math.ceil(this.totalCells / this.perPage));
  }

  get effectiveDots() {
    return window.matchMedia(MOBILE_QUERY).matches
      ? Math.min(2, this.realPageCount)
      : this.realPageCount;
  }

  currentPageIndex() {
    const carousel = this.carousel;
    const offset = Math.abs(carousel.scrollLeft);
    const maxOffset = carousel.scrollWidth - carousel.clientWidth;
    // La última página puede estar incompleta; selectedIndex no siempre es múltiplo de perPage.
    if (maxOffset > 1 && offset >= maxOffset - 1) return this.realPageCount - 1;
    const firstCell = carousel.cells?.[0];
    const gap = parseFloat(getComputedStyle(carousel).columnGap) || 0;
    const pageWidth = firstCell ? (firstCell.getBoundingClientRect().width + gap) * this.columns : 0;
    return Math.min(this.realPageCount - 1, pageWidth > 0 ? Math.round(offset / pageWidth) : 0);
  }

  rebuild() {
    if (!this.carousel || !this.totalCells) {
      this.replaceChildren();
      this.hidden = true;
      return;
    }
    const count = this.effectiveDots;
    this.hidden = this.realPageCount < 2;
    const span = Math.max(1, Math.ceil(this.realPageCount / count));
    const active = Math.min(count - 1, Math.floor(this.currentPageIndex() / span));
    if (this.children.length !== count) {
      this.replaceChildren();
      const fragment = document.createDocumentFragment();
      for (let index = 0; index < count; index++) {
        const button = document.createElement('button');
        button.type = 'button';
        button.classList.add('tap-area');
        button.addEventListener('click', () => {
          const currentSpan = Math.max(1, Math.ceil(this.realPageCount / this.effectiveDots));
          const page = Math.min(index * currentSpan, this.realPageCount - 1);
          this.carousel.select(Math.min(page * this.perPage, this.totalCells - 1));
        }, { signal: this.#abortController.signal });
        fragment.appendChild(button);
      }
      this.appendChild(fragment);
    }
    Array.from(this.children).forEach((button, index) => {
      button.setAttribute('aria-label', `Go to page ${Math.min(index * span, this.realPageCount - 1) + 1}`);
      button.setAttribute('aria-current', String(index === active));
    });
  }
}

// Área del carrusel: muestra la flecha anterior solo cuando hay desplazamiento.
// Shopify sustituye el HTML al editar la sección; el ciclo de conexión
// reinicializa los listeners y desconecta los anteriores.
class MajaCarouselArea extends HTMLElement {
  #abortController;
  #resizeObserver;

  async connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    await Promise.resolve();
    if (signal.aborted || !this.isConnected) return;

    this.#resizeObserver = new ResizeObserver(() => this.updatePrevButtons());
    this.querySelectorAll('scroll-carousel').forEach(carousel => {
      carousel.addEventListener('scroll', () => this.updatePrevButtons(), { passive: true, signal });
      carousel.addEventListener('carousel:select', () => this.updatePrevButtons(), { signal });
      this.#resizeObserver.observe(carousel);
    });
    this.updatePrevButtons();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#resizeObserver?.disconnect();
  }

  updatePrevButtons() {
    // En móvil las flechas nunca se muestran: el CSS las oculta y aquí evitamos
    // forzar display:block inline, que tendría prioridad sobre la hoja de estilos.
    const isMobile = window.matchMedia(MOBILE_QUERY).matches;
    this.querySelectorAll('carousel-prev-button[aria-controls]').forEach(button => {
      const carousel = document.getElementById(button.getAttribute('aria-controls'));
      if (!carousel) return;
      const visible = !isMobile && Math.abs(carousel.scrollLeft) > 1;
      button.style.setProperty('display', visible ? 'block' : 'none', 'important');
    });
  }
}

if (!customElements.get('maja-carousel-dots')) {
  customElements.define('maja-carousel-dots', MajaCarouselDots);
}
if (!customElements.get('maja-carousel-area')) {
  customElements.define('maja-carousel-area', MajaCarouselArea);
}
